import React, { useId, useState } from 'react';
import { Minus, Plus } from 'lucide-react';
import { useTranslation } from '../../lib/i18n';
import { useGenericBi } from '../../lib/use-generic-bi';
import styles from './PricingFaq.module.css';

/**
 * The six questions under the picker.
 *
 * The copy is the logged-out pricing page's own (wix.com/symphony/pricing), so a visitor who read an
 * answer there and then signed in gets the same answer rather than a second, drifting one. That page
 * carries eight; the frame keeps six, dropping "Is Symphony free to use?" and "How does billing
 * work?" — both are questions a signed-in buyer looking at their own prices has already answered.
 *
 * One row open at a time, and the first opens by default, exactly as the frame draws it. A single
 * open row is what keeps the list scannable: six expanded answers is a page of prose where the
 * reader wanted a menu.
 */

const FAQ_KEYS = ['orchestrator', 'credits', 'outOfCredits', 'approval', 'integrations', 'cancel'] as const;

export function PricingFaq() {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi('packages');
  const baseId = useId();
  // The frame draws row 01 open. It is also the question that explains what the product IS, so it is
  // the one worth answering before anybody asks.
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className={styles.section} aria-labelledby={`${baseId}-title`}>
      <h2 id={`${baseId}-title`} className={styles.title}>
        {t('pricing.faq.title')}
      </h2>
      <div className={styles.rows}>
        {FAQ_KEYS.map((key, i) => {
          const open = openIndex === i;
          const Glyph = open ? Minus : Plus;
          const panelId = `${baseId}-panel-${key}`;
          const headId = `${baseId}-head-${key}`;
          return (
            <div key={key} className={styles.row}>
              <h3 style={{ margin: 0 }}>
                <button
                  type="button"
                  id={headId}
                  className={styles.head}
                  aria-expanded={open}
                  aria-controls={panelId}
                  onClick={() => {
                    setOpenIndex(open ? null : i);
                    // `componentName` is the question's KEY, never its text — the text is translated,
                    // and reporting it would split one question across 37 locales in the funnel.
                    logPageClickBi(
                      { page_name: 'packages', button_name: 'faq_toggle' },
                      { componentName: key, additionalFields: { opened: String(!open) } },
                    );
                  }}
                >
                  <span className={styles.number}>{String(i + 1).padStart(2, '0')}</span>
                  <span className={styles.question}>{t(`pricing.faq.${key}.q`)}</span>
                  <Glyph size={24} aria-hidden className={styles.toggle} />
                </button>
              </h3>
              {open && (
                <div id={panelId} role="region" aria-labelledby={headId} className={styles.answer}>
                  <p>{t(`pricing.faq.${key}.a`)}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
